import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';

const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.07, duration: 0.45, ease: 'easeOut' },
    }),
};

const BADGE_COLORS = {
    Nouveau: 'bg-blue-500/20 text-blue-400 border-blue-500/40',
    Bestseller: 'bg-amber-500/20 text-amber-400 border-amber-500/40',
    Populaire: 'bg-purple-500/20 text-purple-400 border-purple-500/40',
    Promo: 'bg-red-500/20 text-red-400 border-red-500/40',
};

const ProductCard = ({ product, index, onAddToCart, className, variant = 'overlay' }) => {
    const [hovered, setHovered] = useState(false);
    const [imgIndex, setImgIndex] = useState(0);
    const [added, setAdded] = useState(false);

    const handleAddToCart = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onAddToCart(product);
        setAdded(true);
        setTimeout(() => setAdded(false), 1500);
    };

    const discount = product.oldPrice
        ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100)
        : null;

    const isWide = variant === 'wide';

    return (
        <motion.div
            custom={index}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            onHoverStart={() => {
                setHovered(true);
                if (product.images.length > 1) setImgIndex(1);
            }}
            onHoverEnd={() => {
                setHovered(false);
                setImgIndex(0);
            }}
            className={`h-full ${className || ''}`}
        >
            <Link to={`/Boutique/${product.slug}`} className="block group h-full">
                <div className="relative rounded-2xl overflow-hidden h-full border border-slate-200 dark:border-store-border hover:border-lime-500/50 transition-all duration-300 hover:shadow-xl dark:hover:shadow-[0_0_30px_rgba(163,230,53,0.1)]">

                    {/* Full-bleed background image */}
                    <motion.img
                        key={imgIndex}
                        src={product.images[imgIndex]}
                        alt={product.name}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.35 }}
                    />

                    {/* Gradient overlay for text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10" />

                    {/* Badge */}
                    {/* {product.badge && (
                        <div
                            className={`absolute top-3 left-3 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold border backdrop-blur-sm ${BADGE_COLORS[product.badge] || 'bg-lime-500/20 text-lime-400 border-lime-500/40'
                                }`}
                        >
                            {product.badge}
                        </div>
                    )} */}

                    {/* Discount */}
                    {discount && (
                        <div className="absolute top-3 right-3 bg-red-500 text-white text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded-full z-10">
                            -{discount}%
                        </div>
                    )}

                    {/* Content positioned at bottom */}
                    <div className="absolute inset-x-0 bottom-0 p-4 z-20 flex flex-col justify-end">
                        {/* Category */}
                        <span className="text-white/50 text-[10px] uppercase tracking-widest font-mono mb-1">
                            {product.category}
                        </span>

                        {/* Name */}
                        <h3 className="text-white font-semibold text-sm leading-snug mb-1 line-clamp-2 group-hover:text-lime-400 transition-colors">
                            {product.name}
                        </h3>

                        {/* Short desc - only on wide cards */}
                        {isWide && (
                            <p className="text-white/60 text-xs line-clamp-1 mb-2">{product.shortDesc}</p>
                        )}

                        {/* Price + CTA */}
                        <div className="flex items-center justify-between mt-1">
                            <div className="line-clamp-1">
                                <span className="text-lime-400 font-bold text-base sm:text-lg">
                                    {product.price.toFixed(2)} $US
                                </span>
                                {product.oldPrice && isWide && (
                                    <span className="text-white/40 line-through text-xs ml-2">
                                        {product.oldPrice.toFixed(2)} $US
                                    </span>
                                )}
                            </div>

                            {product.stock === 0 ? (
                                <span className="text-red-500 text-xs font-semibold border rounded-full px-2 py-1 backdrop-blur-sm bg-red-500/20 ">Rupture</span>
                            ) : (
                                <motion.button
                                    onClick={handleAddToCart}
                                    whileTap={{ scale: 0.9 }}
                                    className={`flex items-center justify-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-xl text-xs font-semibold transition-all duration-300 ${added
                                        ? 'bg-lime-500 text-black shadow-[0_0_12px_rgba(163,230,53,0.5)]'
                                        : 'bg-white/15 hover:bg-white/25 text-white border border-white/20 backdrop-blur-sm'
                                        }`}
                                >
                                    <Icon icon={added ? 'solar:check-circle-bold' : 'solar:cart-plus-bold'} className={`text-sm sm:text-base ${added ? 'text-black' : 'text-lime-500'}`} />
                                    {isWide && <span className="hidden sm:inline">{added ? 'Ajouté !' : 'Ajouter'}</span>}
                                </motion.button>
                            )}
                        </div>
                    </div>
                </div>
            </Link>
        </motion.div>
    );
};

export default ProductCard;
